// Touch bridge: Big J on a phone, with thumbs instead of a keyboard.
//
// The design rule is "gestures over furniture": the screen stays the ship,
// not a cockpit of buttons. Everything maps onto what a phone hand already
// knows how to do —
//
//   left thumb   dynamic joystick (appears under the touch) — analog walk,
//                full deflection breaks into a run once you're moving
//   right thumb  drag to look; the same drag trains the Mk 38 director and
//                lays the turret in local control, because that's what the
//                mouse does on desktop
//   pinch        binoculars. The hold-right-mouse zoom becomes the one
//                gesture every phone user tries first.
//   the prompt   the contextual "E — MAN THE HELM" line becomes the single
//                action button: tap to man a station, hold to fight a fire
//                or take a ladder down. No standing USE button.
//   chips        one slim strip (MAP · LAY TO · GQ · FLARE · CAM · HELP)
//                for the keys that have no gesture. That's the whole HUD tax.
//
// Station consoles grow their own tappable button rows in stations.js; this
// file only owns the world-navigation layer, the gesture router, fullscreen +
// landscape lock on mission start, and the haptic buzzer.

const JOY_R = 58;          // px, joystick throw radius
const DEAD = 0.14;         // deadzone as a fraction of throw
const TAP_MS = 260, TAP_PX = 12;

export function detectTouch() {
  // coarse-and-hoverless is a phone or tablet; ?touch=1 forces it for tests
  if (new URLSearchParams(location.search).has("touch")) return true;
  return window.matchMedia("(pointer: coarse) and (hover: none)").matches;
}

export function initTouch(G) {
  G.touch = detectTouch();
  // The buzzer is defined even on desktop so callers never guard.
  G.buzz = (p) => { if (G.touch) { try { navigator.vibrate?.(p); } catch { /* not every phone hums */ } } };
  if (!G.touch) return;
  document.body.classList.add("touch");

  const player = G.player;
  const canvas = G.renderer.domElement;
  player.pad = { x: 0, y: 0, run: false };

  // --- the touch furniture (built here so the desktop DOM never carries it) --
  const layer = document.createElement("div");
  layer.id = "bb-touch";
  layer.innerHTML = `
    <div id="tj-base" class="hidden"><div id="tj-nub"></div></div>
    <button id="tj-jump" title="jump / climb">▲</button>
    <div id="bb-chips">
      <button data-ui="KeyM">MAP</button>
      <button data-ui="KeyT">LAY TO</button>
      <button data-ui="KeyG">GQ</button>
      <button data-ui="KeyI">FLARE</button>
      <button data-ui="KeyV">CAM</button>
      <button data-ui="KeyH">HELP</button>
    </div>`;
  document.body.appendChild(layer);
  const joyBase = document.getElementById("tj-base");
  const joyNub = document.getElementById("tj-nub");

  // --- chips: same routing as a real keypress (stations get first crack) ----
  for (const b of layer.querySelectorAll("[data-ui]")) {
    b.addEventListener("pointerdown", (e) => {
      e.preventDefault(); e.stopPropagation();
      G.routeKey?.(b.dataset.ui);
    });
  }

  // --- jump / climb: literally holds SPACE, so ladders work unmodified ------
  // Each held button remembers WHICH finger pressed it: a climb up the mast
  // must survive the other thumb tapping and lifting all over the screen.
  const jump = document.getElementById("tj-jump");
  let jumpPid = null;
  const jdown = (e) => { e.preventDefault(); jumpPid = e.pointerId; player.keys.add("Space"); jump.classList.add("held"); };
  const jup = () => { jumpPid = null; player.keys.delete("Space"); jump.classList.remove("held"); };
  jump.addEventListener("pointerdown", jdown);
  jump.addEventListener("pointerup", jup);
  jump.addEventListener("pointercancel", jup);

  // --- the contextual prompt is the action button ---------------------------
  // Holding it holds E (fire fighting, ladders); a quick tap mans the nearest
  // station via the same path the keyboard takes.
  const prompt = document.getElementById("bb-prompt");
  let promptDownT = 0, promptPid = null;
  prompt.addEventListener("pointerdown", (e) => {
    e.preventDefault(); e.stopPropagation();
    promptDownT = performance.now();
    promptPid = e.pointerId;
    player.keys.add("KeyE");
    prompt.classList.add("held");
    // the ladder-down prompt works by intent: E + a moment of SHIFT while
    // slow is exactly the desktop "reach for the trunk" grab
    if (prompt.dataset.ladder) player.keys.add("ShiftLeft");
  });
  const promptUp = () => {
    if (!promptDownT) return;
    const quick = performance.now() - promptDownT < 420;
    promptDownT = 0; promptPid = null;
    player.keys.delete("KeyE");
    player.keys.delete("ShiftLeft");
    prompt.classList.remove("held");
    if (quick && !prompt.dataset.ladder) G.useAction?.();
  };
  prompt.addEventListener("pointerup", promptUp);
  prompt.addEventListener("pointercancel", promptUp);

  // --- gesture router on the canvas -----------------------------------------
  // One finger left half: joystick. One finger elsewhere: look. Two fingers:
  // pinch zoom. All state keyed by pointerId so a thumb can join mid-gesture.
  const touches = new Map(); // id -> {x0,y0,x,y,t0,role}
  let joyId = null, lookId = null;
  let pinch = null; // {d0, zoom0, kind}

  const zoomKind = () => {
    const st = G.stations.active;
    if (st?.kind === "director") return "director";
    if (st) return null;               // other consoles: pinch does nothing
    return "binoc";
  };

  function startPinch(idA, idB) {
    const a = touches.get(idA), b = touches.get(idB);
    if (!a || !b) return;
    const kind = zoomKind();
    if (!kind) return;
    pinch = {
      ids: [idA, idB],
      d0: Math.hypot(a.x - b.x, a.y - b.y),
      zoom0: kind === "director" ? G.stations.dirZoom : player.zoom,
      kind,
    };
    a.pinched = b.pinched = true; // a pinch finger never counts as a tap
    // both fingers were look fingers; the pinch owns them now. The joystick
    // thumb keeps walking — two thumbs is the whole point of two thumbs.
    lookId = null; G.stations.dragSlew = false;
  }

  function movePinch() {
    const a = touches.get(pinch.ids[0]), b = touches.get(pinch.ids[1]);
    if (!a || !b) { pinch = null; return; }
    const r = Math.hypot(a.x - b.x, a.y - b.y) / Math.max(40, pinch.d0);
    if (pinch.kind === "director") {
      G.stations.dirZoom = Math.max(8, Math.min(25, pinch.zoom0 * r));
    } else {
      let z = Math.max(1, Math.min(8, pinch.zoom0 * r));
      if (z < 1.25) z = 1; // snap the glasses back into the case
      player.zoom = z;
    }
  }

  function showJoy(x, y) {
    joyBase.classList.remove("hidden");
    joyBase.style.left = `${x}px`;
    joyBase.style.top = `${y}px`;
    joyNub.style.transform = "translate(0px, 0px)";
  }
  function hideJoy() {
    joyBase.classList.add("hidden");
    player.pad.x = 0; player.pad.y = 0; player.pad.run = false;
  }

  // Drag-to-look, routed the way mousemove is on desktop: the director takes
  // the drag as a slew, a locally-controlled turret takes it as train/elevate,
  // everyone else gets their head.
  function applyLook(dx, dy) {
    const st = G.stations.active;
    if (st?.kind === "director") {
      // hand on the glass: auto-track lets go while the finger drags
      G.stations.dragSlew = true;
      G.stations.viewYaw += dx * 0.0011 / (G.stations.dirZoom / 8);
      return;
    }
    if (st?.kind === "turret") {
      const t = G.ship.turrets[1];
      if (G.guns.localTurret === 1) {
        if (!t.localOrder) t.localOrder = { train: t.train, elev: t.elev };
        t.localOrder.train = Math.atan2(
          Math.sin(t.localOrder.train + dx * 0.0006), Math.cos(t.localOrder.train + dx * 0.0006));
        t.localOrder.elev = Math.max(0, Math.min(0.785, t.localOrder.elev - dy * 0.0005));
      }
      return;
    }
    if (st) return;
    player.look(dx * 1.7, dy * 1.7);
  }

  // Role assignment. The first finger in the lower-left zone is the
  // joystick; any other finger is a look drag; a SECOND look finger turns
  // both into a pinch. The joystick thumb never joins a pinch and a pinch
  // never steals it — the original build collapsed ANY two fingers into a
  // pinch, which read as "the ship refuses to let me walk and look at
  // once": the stick died the instant the right thumb landed, and stayed
  // dead until every finger lifted. Fingers beyond joy+look+pinch idle.
  canvas.addEventListener("pointerdown", (e) => {
    if (e.pointerType !== "touch") return;
    e.preventDefault();
    touches.set(e.pointerId, { x0: e.clientX, y0: e.clientY, x: e.clientX, y: e.clientY, t0: performance.now() });
    const walking = !G.stations.active;
    if (walking && joyId === null && !pinch
      && e.clientX < innerWidth * 0.44 && e.clientY > innerHeight * 0.28) {
      joyId = e.pointerId;
      showJoy(e.clientX, e.clientY);
    } else if (lookId === null && !pinch) {
      lookId = e.pointerId;
    } else if (lookId !== null && !pinch) {
      startPinch(lookId, e.pointerId);
    }
  }, { passive: false });

  canvas.addEventListener("pointermove", (e) => {
    if (e.pointerType !== "touch") return;
    const t = touches.get(e.pointerId);
    if (!t) return;
    const dx = e.clientX - t.x, dy = e.clientY - t.y;
    t.x = e.clientX; t.y = e.clientY;
    if (pinch) { movePinch(); return; }
    if (e.pointerId === joyId) {
      let jx = e.clientX - t.x0, jy = e.clientY - t.y0;
      const m = Math.hypot(jx, jy);
      if (m > JOY_R) { jx *= JOY_R / m; jy *= JOY_R / m; }
      joyNub.style.transform = `translate(${jx}px, ${jy}px)`;
      const nx = jx / JOY_R, ny = jy / JOY_R;
      const mag = Math.min(1, m / JOY_R);
      player.pad.x = Math.abs(nx) < DEAD ? 0 : nx;
      player.pad.y = Math.abs(ny) < DEAD ? 0 : -ny;
      player.pad.run = mag > 0.94;
    } else if (e.pointerId === lookId) {
      applyLook(dx, dy);
    }
  }, { passive: false });

  const end = (e) => {
    if (e.pointerType !== "touch") return;
    const t = touches.get(e.pointerId);
    touches.delete(e.pointerId);
    if (pinch && pinch.ids.includes(e.pointerId)) {
      // the surviving pinch finger goes back to being a look drag, so the
      // glasses hand off to the head without a lift-and-retouch
      const other = pinch.ids.find((id) => id !== e.pointerId);
      pinch = null;
      if (other !== undefined && touches.has(other) && lookId === null) {
        const o = touches.get(other);
        o.x0 = o.x; o.y0 = o.y; // a pinch remnant is never a tap either
        lookId = other;
      }
    }
    if (e.pointerId === joyId) { joyId = null; hideJoy(); }
    if (e.pointerId === lookId) { lookId = null; G.stations.dragSlew = false; }
    // a clean tap on the world (no drag, no pinch, no station, quick) = use
    // action — walk up to the helm and just tap it
    if (t && !t.pinched && !G.stations.active && e.type === "pointerup"
      && performance.now() - t.t0 < TAP_MS
      && Math.hypot(e.clientX - t.x0, e.clientY - t.y0) < TAP_PX) {
      const st = G.stations.nearest();
      if (st) G.useAction?.();
    }
  };
  canvas.addEventListener("pointerup", end);
  canvas.addEventListener("pointercancel", end);

  // Stray pointerups anywhere (overlays swallowing the up, re-rendered
  // buttons) must never leave a phantom key held down — but only the finger
  // that pressed a button gets to release it, or the look thumb lifting
  // would drop a held climb or a fire-fighting hold mid-job.
  const strayUp = (e) => {
    if (e.pointerId === promptPid) promptUp();
    if (e.pointerId === jumpPid) jup();
  };
  window.addEventListener("pointerup", strayUp);
  window.addEventListener("pointercancel", strayUp);

  // Stations freeze the walker: park the joystick when a console is manned so
  // its buttons own the bottom of the screen. stations.js flips this class.
  // (Done via observer so stations.js stays ignorant of this module.)
  new MutationObserver(() => {
    const on = !document.getElementById("bb-station").classList.contains("hidden");
    document.body.classList.toggle("station-on", on);
    if (on) { hideJoy(); joyId = null; }
  }).observe(document.getElementById("bb-station"), { attributes: true, attributeFilter: ["class"] });
}

// Mission start on a phone: go all in on the native app feel — fullscreen,
// locked to landscape. Both calls are best-effort (iOS Safari says no to
// both and the game must not care).
export function enterMobileFullscreen() {
  try { document.documentElement.requestFullscreen?.({ navigationUI: "hide" })?.catch(() => {}); } catch { /* iOS */ }
  try { screen.orientation?.lock?.("landscape")?.catch(() => {}); } catch { /* not fullscreen yet, or iOS */ }
}
